import Auth from "../routes/Auth";
import { reactLocalStorage } from "reactjs-localstorage";

export const COLORS_LEVEL1 = [
  "#8dd9de",
  "#4dc8ff",
  "#fcefd6",
  "#f3eefe",
  "#f9bcbc",
  "#b0e1b1",
  "#f9dec7",
  "#c6eef9",
  "#aef5f0",
  "#f2f3ad"
];
const COLORS_LEVEL2 = [
  "#7ad2d8",
  "#33c0ff",
  "#fae6bf",
  "#e2d5fe",
  "#f6a5a5",
  "#9dda9f",
  "#f7d1b0",
  "#afe7f6",
  "#98f2ec",
  "#eff096"
];
const COLORS_LEVEL3 = [
  "#52c5cd",
  "#00b0ff",
  "#f7d48f",
  "#bfa4fc",
  "#f27676",
  "#78cc7b",
  "#f2b682",
  "#81d9f1",
  "#81efe8",
  "#ebed80"
];
const COLORS_LEVEL4 = [
  "#35afb7",
  "#008dcc",
  "#f4c25f",
  "#9c73fa",
  "#ee4747",
  "#53be57",
  "#ed9b54",
  "#53cbec",
  "#54e9e0",
  "#e4e753"
];
const COLORS_LEVEL0 = [
  "#2f9ca3",
  "#007bb3",
  "#f2b948",
  "#8b5afa",
  "#eb3030",
  "#44b448",
  "#eb8e3d",
  "#3cc4e9",
  "#3de6dc",
  "#e0e43d"
];

export const TEAM_COLORS = COLORS_LEVEL3;
const RESOLUTION_COLORS = COLORS_LEVEL1;
const SENSITIVITY_COLORS = COLORS_LEVEL2;
const POWER_COLORS = COLORS_LEVEL3;
const TEMPERATURE_COLORS = COLORS_LEVEL4;
export const TRADITIONAL_MARKETING_COLORS = COLORS_LEVEL0;
export const DIGITAL_MARKETING_COLORS = COLORS_LEVEL4;
export const DIRECT_SALES_COLORS = COLORS_LEVEL3;
export const DISTRIBUTORS_COLORS = COLORS_LEVEL2;
export const PRODUCT_COSTS_COLORS = {
  "Material Costs": COLORS_LEVEL0,
  "Labor Costs": COLORS_LEVEL3,
  "Overhead Costs": COLORS_LEVEL4
};
export const CAPITAL_EXPENDITURES_COLORS = {
  "Production Line 4": COLORS_LEVEL0,
  "Production Line 3": COLORS_LEVEL4,
  "Production Line 2": COLORS_LEVEL3,
  "Production Line 1": COLORS_LEVEL2,
  "Production Facility": COLORS_LEVEL0,
  Factory: COLORS_LEVEL0
};
export const DEBTS_COLORS = [
  [COLORS_LEVEL2[0], COLORS_LEVEL3[0], COLORS_LEVEL4[0]],
  [COLORS_LEVEL2[1], COLORS_LEVEL3[1], COLORS_LEVEL4[1]],
  [COLORS_LEVEL2[2], COLORS_LEVEL3[2], COLORS_LEVEL4[2]],
  [COLORS_LEVEL2[3], COLORS_LEVEL3[3], COLORS_LEVEL4[3]],
  [COLORS_LEVEL2[4], COLORS_LEVEL3[4], COLORS_LEVEL4[4]],
  [COLORS_LEVEL2[5], COLORS_LEVEL3[5], COLORS_LEVEL4[5]],
  [COLORS_LEVEL2[6], COLORS_LEVEL3[6], COLORS_LEVEL4[6]],
  [COLORS_LEVEL2[7], COLORS_LEVEL3[7], COLORS_LEVEL4[7]],
  [COLORS_LEVEL2[8], COLORS_LEVEL3[8], COLORS_LEVEL4[8]],
  [COLORS_LEVEL2[9], COLORS_LEVEL3[9], COLORS_LEVEL4[9]]
];
export const EQUITY_COLORS = COLORS_LEVEL0;
export const COMPETITIVE_COLORS = COLORS_LEVEL3;
const UNSOLD_COLORS = COLORS_LEVEL1;
export const UNMET_DEMAND_COLOR = "#36454f";

// Team color assignment lives in a single competition-level map:
//   { [teamId]: paletteIndex }
// The map is computed ONCE from the user's competition teams (sorted by
// id ascending) and persisted to localStorage. Every consumer — topbar
// avatar, every chart, every table — resolves colors through the same
// map, so the same team always renders the same color regardless of
// which endpoint's teams array the caller happens to hold.
//
// The palettes in this file all have 10 slots and a competition holds
// at most 7 teams, so every team inside the competition gets a distinct
// slot with room to spare.

const TEAM_COLOR_INDEX_MAP_KEY = "team_color_index_map";

export const sortTeamsById = (teams) =>
  [...(teams || [])].sort((a, b) => Number(a.id) - Number(b.id));

export const computeTeamColorIndexMap = (teams) => {
  const map = {};
  sortTeamsById(teams).forEach((t, idx) => {
    if (t && t.id != null) {
      map[String(t.id)] = idx;
    }
  });
  return map;
};

// Read the persisted competition color-index map. Returns {} if missing
// so callers can treat the return value like a normal object.
export const getTeamColorIndexMap = () => {
  const obj = reactLocalStorage.getObject(TEAM_COLOR_INDEX_MAP_KEY);
  return obj && typeof obj === "object" ? obj : {};
};

// Overwrite the persisted competition color-index map. Skips the write
// when the caller has nothing to persist so we don't blow away an
// already-good map with an empty object during a race condition (e.g.
// a fetch that returned zero rows for the current round).
export const persistTeamColorIndexMap = (teams) => {
  if (!teams || teams.length === 0) return;
  const map = computeTeamColorIndexMap(teams);
  if (Object.keys(map).length > 0) {
    reactLocalStorage.setObject(TEAM_COLOR_INDEX_MAP_KEY, map);
  }
};

export const colorForTeamId = (teams, teamId, palette = COMPETITIVE_COLORS) => {
  if (teamId == null) return "";
  // Prefer Teams.teams() (localStorage teams_data filtered by the
  // user's competition) so every student-facing consumer — topbar
  // avatar, every chart, every table — computes against the exact same
  // list and resolves the same team to the same color. Instructors
  // don't have a "current team", so Teams.teams() returns [] for them —
  // fall back to the caller's `teams` argument in that case so
  // instructor charts (performance-feedback pages, dashboards) still
  // get colored bars instead of the default black.
  const currentTeams = Teams.teams();
  const source = currentTeams.length > 0 ? currentTeams : (teams || []);
  const map = computeTeamColorIndexMap(source);
  const idx = map[String(teamId)];
  if (idx == null) return "";
  return palette[idx % palette.length];
};

// Every /api/teams/ caller should route through this helper so the
// request is scoped to the current game only (Auth.getGameId() was set
// at login to the highest-id game in the user's latest course). Without
// the ?game filter the BE returns teams for every game the user has
// access to, which pollutes downstream charts + roster views on
// multi-game instructor accounts.
export const teamsEndpoint = () => {
  const gameId = Auth.getGameId();
  const base = `${process.env.REACT_APP_API_URL}teams/`;
  return gameId ? `${base}?game=${gameId}` : base;
};

class Teams {
  static requests() {
    const team_id = Auth.getTeamId();

    return [
      `${process.env.REACT_APP_API_URL}teams/${team_id}/players/`,
      teamsEndpoint()
    ];
  }

  static setData(teams_data) {
    if (teams_data[0].status !== 200 || teams_data[1].status !== 200) {
      let game_is_not_started = false;
      teams_data.forEach(team_data => {
        if (
          team_data.status === 403 &&
          "detail" in team_data.data &&
          team_data.data.detail === "Game is not started"
        ) {
          game_is_not_started = true;
        }
      });

      if (game_is_not_started === true) {
        Teams.setEmptyData();
        return "Game is not started";
      } else {
        return "Error";
      }
    }

    reactLocalStorage.setObject(
      "players_data",
      teams_data[0].data.filter(player => "user" in player)
    );
    reactLocalStorage.setObject("teams_data", teams_data[1].data);

    // Seed the competition color-index map so the topbar and any page
    // that renders before Analysis loads (e.g. decision pages) share
    // the same per-team palette assignment. Filter to the user's
    // competition first so palette slots are packed contiguously
    // starting at 0 for the ≤7 teams the user will actually see.
    const teamId = Auth.getTeamId();
    const allTeams = teams_data[1].data || [];
    const self = allTeams.find(t => Number(t.id) === Number(teamId));
    const competitionTeams = self
      ? allTeams.filter(t => t.competition === self.competition)
      : allTeams;
    persistTeamColorIndexMap(competitionTeams);

    return "Success";
  }

  static setEmptyData() {
    reactLocalStorage.setObject("players_data", []);
    reactLocalStorage.setObject("teams_data", []);
    reactLocalStorage.setObject(TEAM_COLOR_INDEX_MAP_KEY, {});
  }

  static getObject(key) {
    const obj = reactLocalStorage.getObject(key);

    if (typeof obj === "object" && Object.keys(obj).length === 0) {
      return [];
    }

    return obj;
  }

  static players() {
    return Teams.getObject("players_data");
  }

  static currentPlayer() {
    return Teams.players().find(
      i => "user" in i && i.user.id === Number(Auth.getUserId())
    );
  }

  static currentTeam() {
    const team_id = Auth.getTeamId();

    if (!team_id) {
      return null;
    }

    return Teams.getObject("teams_data").find(i => i.id === team_id);
  }

  static currentTeamIndex() {
    const team_id = Auth.getTeamId();

    if (!team_id) {
      return -1;
    }

    // Compute the color-index map directly from Teams.teams() every
    // time so this method and colorForTeamId are always looking at the
    // exact same list. See colorForTeamId at the top of this file for
    // the full rationale.
    const map = computeTeamColorIndexMap(Teams.teams());
    const idx = map[String(team_id)];
    return idx == null ? -1 : idx;
  }

  static currentTeamColor() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    return TEAM_COLORS[index % TEAM_COLORS.length];
  }

  static currentTeamColorLevel1() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    return COLORS_LEVEL1[index % COLORS_LEVEL1.length];
  }

  static currentTeamColorLevel3() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    return COLORS_LEVEL3[index % COLORS_LEVEL3.length];
  }

  static teams() {
    const team = Teams.currentTeam();

    if (!team) {
      return [];
    }

    return Teams.getObject("teams_data").filter(
      i => i.competition === team.competition
    );
  }

  static productDevelopmentColor(type) {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    if (type === "bg-resolution") {
      return RESOLUTION_COLORS[index % RESOLUTION_COLORS.length];
    } else if (type === "bg-sensitivity") {
      return SENSITIVITY_COLORS[index % SENSITIVITY_COLORS.length];
    } else if (type === "bg-power") {
      return POWER_COLORS[index % POWER_COLORS.length];
    } else if (type === "bg-temperature") {
      return TEMPERATURE_COLORS[index % TEMPERATURE_COLORS.length];
    }

    return "";
  }

  static traditionalMarketingColor() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    return TRADITIONAL_MARKETING_COLORS[
      index % TRADITIONAL_MARKETING_COLORS.length
    ];
  }

  static digitalMarketingColor() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    return DIGITAL_MARKETING_COLORS[index % DIGITAL_MARKETING_COLORS.length];
  }

  static directSalesColor() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    return DIRECT_SALES_COLORS[index % DIRECT_SALES_COLORS.length];
  }

  static distributorsColor() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    return DISTRIBUTORS_COLORS[index % DISTRIBUTORS_COLORS.length];
  }

  static capitalExpendituresColor(key) {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    if (key in CAPITAL_EXPENDITURES_COLORS) {
      const colors = CAPITAL_EXPENDITURES_COLORS[key];

      return colors[index % colors.length];
    }

    return "";
  }

  static productCostsColor(key) {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    if (key in PRODUCT_COSTS_COLORS) {
      const colors = PRODUCT_COSTS_COLORS[key];

      return colors[index % colors.length];
    }

    return "";
  }

  static debtsColors() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return ["", "", ""];
    }

    return DEBTS_COLORS[index % DEBTS_COLORS.length];
  }

  static equityColor() {
    const index = Teams.currentTeamIndex();

    if (index < 0) {
      return "";
    }

    return EQUITY_COLORS[index % EQUITY_COLORS.length];
  }

  static competitiveColors(teams = null) {
    const currentTeams =
      teams && Auth.getPosition() !== "Student" ? teams : Teams.teams();
    // Preserve the caller's team order in the returned array (so it
    // maps 1:1 with the input) but pick each team's color from the
    // sorted-by-id position so the same team always resolves to the
    // same color regardless of how the caller sorted its list.
    return currentTeams.map(team => colorForTeamId(currentTeams, team.id, COMPETITIVE_COLORS));
  }

  static unsoldColors() {
    const teams = Teams.teams();
    return teams.map(team => colorForTeamId(teams, team.id, UNSOLD_COLORS));
  }
}

export default Teams;



// WEBPACK FOOTER //
// ./src/api/Teams.js